/**
 * @fileoverview Vuex module para gerenciamento de pastas/processos.
 *
 * Centraliza o estado de pastas na aplicação, incluindo:
 * - Lista de pastas por caixa (inbox, sent, archived, unreceived)
 * - Pasta ativa (sendo visualizada)
 * - Contagens por caixa para badges
 * - Tipos de pasta para filtros
 * - Modo de visualização (lista/grid)
 *
 * Este é o módulo Vuex mais importante da aplicação, usado
 * em praticamente todas as telas de gestão de processos.
 *
 * @module vuex/folders
 * @see {@link module:services/FoldersService} para chamadas de API
 * @see {@link module:typedef~Folder} para definição de Folder
 */

import foldersService from "@/services/FoldersService";
import { chain } from "lodash";
import companyLogo from "../../utils/company-logo-url";

/**
 * Vuex module de pastas.
 *
 * @namespace folder
 * @property {Object} state - Estado do módulo
 * @property {Folder} state.activeFolder - Pasta atualmente selecionada
 * @property {Folder[]} state.folders - Lista de pastas da view atual
 * @property {Object} state.foldersCount - Contagens por caixa
 * @property {string} state.folderViewMode - Modo de visualização ('list'|'grid')
 */
const folder = {
  state: {
    draggingFolderId: null,
    activeFolder: {
      activity_logs: [],
      attached_folders: {
        folders: [],
        status: ""
      },
      comments: [],
      confidential: false,
      created_at: "",
      current_owner: {},
      documents: [],
      entity: null,
      created_in: [],
      folder_status: {},
      folder_type: {},
      id: "",
      interesteds_citizens: [],
      interesteds_users: [],
      last_modification_at: "",
      number: "",
      ownership_requested: false,
      process_number: "",
      subject: "",
      transactions: [],
      user: {}
    },
    activeFolderFilterType: {},
    folders: {},
    foldersFound: [],
    foldersCount: {
      archived: 0,
      inbox: 0,
      sent: 0,
      unreceived: 0,
      revisions: 0,
      signatures: 0
    },
    sentCounts: {
      total: 0,
      received: 0,
      waiting: 0,
      refused: 0
    },
    inboxCounts: {
      total: 0,
      received: 0,
      waiting: 0,
      refused: 0
    },
    unreceivedCounts: {
      total: 0,
      received: 0,
      waiting: 0,
      refused: 0
    },
    activeTotalFolders: 0,
    folderTypes: {},
    folderViewMode: "list",
    foldersPagesCount: 0,
    folderIdCounter: 0,
    callCounter: 0,
    isFoldersCountLoading: false,
    lastCountUpdate: null,
    selectedAdministrativeUnit: null,
    generatingAiResume: false
  },
  getters: {
    activeFolder(state) {
      return state.activeFolder;
    },
    activeFolderFilterType(state) {
      return state.activeFolderFilterType;
    },
    folders(state) {
      return state.folders;
    },
    foldersFound(state) {
      return state.foldersFound;
    },
    foldersCount(state) {
      return state.foldersCount;
    },
    sentCounts(state) {
      return state.sentCounts;
    },
    inboxCounts(state) {
      return state.inboxCounts;
    },
    unreceivedCounts(state) {
      return state.unreceivedCounts;
    },
    folderViewMode(state) {
      return state.folderViewMode;
    },
    folderTypes(state) {
      return state.folderTypes;
    },
    foldersPagesCount(state) {
      return state.foldersPagesCount;
    },
    activeTotalFolders(state) {
      return state.activeTotalFolders;
    },
    draggingFolderId(state) {
      return state.draggingFolderId;
    },
    folderIdCounter(state) {
      return state.folderIdCounter;
    },
    callCounter(state) {
      return state.callCounter;
    },
    isFoldersCountLoading(state) {
      return state.isFoldersCountLoading;
    },
    lastCountUpdate(state) {
      return state.lastCountUpdate;
    },

    isGeneratingAiResume(state) {
      return state.generatingAiResume;
    }
    // selectedAdministrativeUnit(state) {
    //   return state.selectedAdministrativeUnit;
    // }
  },
  mutations: {
    incrementCallCounter(state) {
      state.callCounter++;
    },
    setFolderIdCounter(state, folderId) {
      state.folderIdCounter = folderId;
    },
    setDraggingFolder(state, folderId) {
      state.draggingFolderId = folderId;
    },
    clearDragDrop(state) {
      state.draggingFolderId = null;
    },
    setFolders(state, payload) {
      state.folders = payload;
    },
    setFoldersFound(state, payload) {
      state.foldersFound = [...payload];
    },
    setFolderTypes(state, payload) {
      state.folderTypes = [...payload];
    },
    setFolderAiResume(state, payload) {
      state.activeFolder = { ...state.activeFolder, ai_resume: payload };
    },
    setGeneratingAiResume(state, payload) {
        state.generatingAiResume = payload;
    },
    setActiveFolder(state, payload) {
      state.activeFolder = { ...payload };
    },
    setActiveFolderFilterType(state, payload) {
      state.activeFolderFilterType = { ...payload };
      state.foldersFound = state.folders.filter(
        item => item.folder_type.id === payload.id
      );
    },
    setFolderNumber(state, payload) {
      state.activeFolder = { ...state.activeFolder, number: payload };
    },
    setFolderSubject(state, payload) {
      state.activeFolder = { ...state.activeFolder, subject: payload };
    },
    setFoldersCount(state, payload) {
      state.foldersCount = payload;
      state.lastCountUpdate = new Date().toISOString();
    },
    setSentCounts(state, payload) {
      state.sentCounts = payload;
    },
    setInboxCounts(state, payload) {
      state.inboxCounts = payload;
    },
    setUnreceivedCounts(state, payload) {
      state.unreceivedCounts = payload;
    },
    setFoldersCountLoading(state, isLoading) {
      state.isFoldersCountLoading = isLoading;
    },
    setActiveTotalFolders(state, payload) {
      state.activeTotalFolders = payload;
    },
    setFolderViewMode(state, payload) {
      state.folderViewMode = payload;
    },
    EMPTY_FOLDERS(state) {
      state.folders = [];
      state.foldersFound = [];
      state.activeFolder = {};
      state.foldersCount = {};
      state.sentCounts = {
        total: 0,
        received: 0,
        waiting: 0,
        refused: 0
      };
      state.inboxCounts = {
        total: 0,
        received: 0,
        waiting: 0,
        refused: 0
      };
      state.unreceivedCounts = {
        total: 0,
        received: 0,
        waiting: 0,
        refused: 0
      };
    },
    setFoldersPageCount(state, payload) {
      state.foldersPagesCount = payload;
    },
    setInteresteds(state, payload) {
      state.activeFolder.interesteds_users = payload.interesteds_users;
      state.activeFolder.interesteds_citizens = payload.interesteds_citizens;
    }
  },
  actions: {
    async getFoldersArchived({ commit }, payload = { page: 1, limit: 5 }) {
      const { page, limit, searchText } = payload;
      const response = await foldersService.getFoldersArchived(
        page,
        limit,
        searchText
      );
      const archived = chain(response.data.archived)
        .map(item => ({
          ...item,
          ...item.folder,
          created_at: item.created_at,
          folder_section: "archived"
        }))
        .value();
      commit("setFoldersPageCount", response.data.total_pages);
      commit("setActiveTotalFolders", response.data.total);
      commit("setFolders", archived);
    },
    async getFoldersInbox(
      { commit },
      payload = { page: 1, limit: 5, order: "asc", sort: "limit_date" }
    ) {
      const { page, limit, order, sort, searchText, folderTypeId, administrativeUnitId } = payload;
      const response = await foldersService.getFoldersInbox(
        page,
        limit,
        order,
        sort,
        searchText,
        folderTypeId,
        administrativeUnitId
      );
      const inbox = chain(response.data.inbox)
        .map(item => ({
          ...item,
          ...item.folder,
          created_at: item.created_at,
          folder_section: "inbox"
        }))
        .value();
      console.log("getFoldersInbox: ", inbox)
      const folderTypes = chain(response.data.folder_types_on_inbox)
      .map(item => ({
        ...item,
        id: item.id,
        name: item.name
      })).value();
      commit("setFoldersPageCount", response.data.total_pages);
      commit("setActiveTotalFolders", response.data.total);
      commit("setFolders", inbox);
      commit("setFolderTypes", folderTypes);
      
      // Armazenar as contagens do backend
      if (response.data.counts) {
        commit("setInboxCounts", response.data.counts);
      }
    },
    async getFoldersUnreceived(
      { commit },
      payload = { page: 1, limit: 5, order: "asc", sort: "limit_date" }
    ) {
      const { page, limit, order, sort, searchText, folderTypeId, administrativeUnitId } = payload;
      const response = await foldersService.getFoldersUnreceived(
        page,
        limit,
        order,
        sort,
        searchText,
        folderTypeId,
        administrativeUnitId
      );
      const inbox = chain(response.data.unreceived)
        .map(item => ({
          ...item,
          ...item.folder,
          created_at: item.created_at,
          folder_section: "inbox"
        }))
        .value();
      const folderTypes = chain(response.data.unreceived)
      .map(item => ({
        ...item,
        id: item.id,
        name: item.name
      })).value();
      commit("setFoldersPageCount", response.data.total_pages);
      commit("setActiveTotalFolders", response.data.total);
      commit("setFolders", inbox);
      commit("setFolderTypes", folderTypes);
      
      // Armazenar as contagens do backend
      if (response.data.counts) {
        commit("setUnreceivedCounts", response.data.counts);
      }
    },
    async getCustomFolders(
      { commit },
      payload = {
        id: null,
        page: 1,
        limit: 5,
        order: "asc",
        sort: "limit_date"
      }
    ) {
      const {
        id,
        page,
        limit,
        order,
        sort,
        searchText,
        folderTypeId
      } = payload;
      const response = await foldersService.getFoldersCustom(
        id,
        page,
        limit,
        order,
        sort,
        searchText,
        folderTypeId
      );
      const inbox = chain(response.data.folders)
        .map(item => ({
          ...item,
          ...item.folder,
          created_at: item.created_at,
          folder_section: "inbox"
        }))
        .value();
      commit("setFoldersPageCount", response.data.total_pages);
      commit("setActiveTotalFolders", response.data.total);
      commit("setFolders", inbox);
    },
    async getFoldersSearch({ commit }, payload = { page: 1, limit: 5 }) {
      const { page, limit, searchText } = payload;
      const response = await foldersService.getFoldersSearch(
        page,
        limit,
        searchText
      );

      // console.log('getFoldersSearch response', response.data);
      const search = chain(response.data.folders || response.data.search)
        .map(item => ({
          ...item,
          ...item.folder,
          created_at: item.created_at,
          folder_section: "search"
        }))
        .value();
      commit("setFoldersPageCount", response.data.total_pages);
      commit("setActiveTotalFolders", response.data.total);
      commit("setFolders", search);
    },
    async getFoldersSent({ commit }, payload = { page: 1, limit: 5 }) {
      const { page, limit, searchText, administrativeUnitId } = payload;
      const response = await foldersService.getFoldersSent(
        page,
        limit,
        searchText,
        administrativeUnitId
      );
      commit("setFoldersPageCount", response.data.total_pages);
      commit("setActiveTotalFolders", response.data.total);
      commit("setFolders", response.data.sent);
      
      // Armazenar as contagens do backend
      if (response.data.counts) {
        commit("setSentCounts", response.data.counts);
      }
    },
    async getFoldersPending({ commit }) {
      const foldersToSignResponse = await foldersService.getFoldersToSign();
      // console.log("foldersToSignResponse", foldersToSignResponse);

      const foldersToSign = foldersToSignResponse.data.pendencies.map(item => {
        // Agora o backend sempre retorna o nó documents corretamente
        const docs = item.documents ? item.documents.filter(
          d => d.has_pending_signatures === true
        ) : [];
        return {
          ...item,
          folder_section: "pending",
          pending_type: "signature",
          pending_date: docs.length > 0 && docs[0].signature_date ? docs[0].signature_date : null
        };
      });
      const foldersToReviewResponse = await foldersService.getFoldersToReview();
      const foldersToReview = foldersToReviewResponse.data.pendencies.map(
        item => {
          const date = item.last_transaction ? item.last_transaction.created_at : 'Sem data de revisão';
          return {
            ...item,
            folder_section: "pending",
            pending_type: "review",
            pending_date: date
          };
        }
      );
      commit("setFolders", [...foldersToSign, ...foldersToReview]);
    },
    async getFoldersCount({ commit, state, getters }, options = {}) {
      // Opção para forçar atualização independente do timer
      const { forceUpdate = false } = options;

      // Verifica se já existe uma solicitação em andamento
      if (state.isFoldersCountLoading) {
        return;
      }

      // Verifica se a última atualização foi há menos de 30 segundos (a menos que seja forçada)
      if (!forceUpdate) {
        const now = new Date();
        if (state.lastCountUpdate) {
          const lastUpdate = new Date(state.lastCountUpdate);
          const diffMs = now - lastUpdate;
          if (diffMs < 30000) { // 30 segundos
            return;
          }
        }
      }

      try {
        commit("setFoldersCountLoading", true);

        // Obtém a unidade administrativa selecionada do getter
        const administrativeUnitId = getters.selectedAdministrativeUnit || '';

        const response = await foldersService.getFoldersCount(administrativeUnitId);
        commit("setFoldersCount", response.data);
      } catch (error) {
        console.error("Erro ao buscar contagem de pastas:", error);
      } finally {
        commit("setFoldersCountLoading", false);
      }
    },
    setFolderTypes({ commit }, payload) {
      commit("setFolderTypes", payload);
    },
    setFolders({ commit }, payload) {
      commit("setFolders", payload);
    },
    setFoldersFound({ commit }, payload) {
      commit("setFoldersFound", payload);
    },
    async getActiveFolder({ commit }, payload) {
      return new Promise((resolve, reject) => {
        foldersService
          .getFolder(payload.id)
          .then(response => {
            commit("toggleLoader", { loading: false });
            commit("setActiveFolder", response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    generateAiResume({ commit }, payload) {
      commit('setGeneratingAiResume', true);

      return foldersService.generateAiResume(payload)
          .then(response => {
            console.log('response', response)
            commit("setFolderAiResume", response.data.text);
          })
          .catch(error => {
            commit("toggleSnackbar", {
                show: true,
                timeout: 6000,
                color: "error",
                message: error.message || 'Erro ao gerar resumo',
                messageIcon: "mdi-alert-outline"
            });
          })
          .finally(() => {
            setTimeout(() => commit('setGeneratingAiResume', false), 300);
          });
    },
    addNumber({ commit }, payload) {
      foldersService
        .addNumber(payload)
        .then(response => {
          commit("setFolderNumber", payload.folder.number);
          commit("toggleSnackbar", {
            show: true,
            timeout: 6000,
            color: "success",
            message: response.data.message,
            messageIcon: "mdi-check"
          });
        })
        .catch(error => {
          commit("toggleSnackbar", {
            show: true,
            timeout: 6000,
            color: "error",
            message: error.message,
            messageIcon: "mdi-alert-outline"
          });
        });
    },
    updateSubject({ commit }, payload) {
      foldersService
        .updateSubject(payload)
        .then(response => {
          commit("setFolderSubject", payload.folder.subject);
          commit("toggleSnackbar", {
            show: true,
            timeout: 6000,
            color: "success",
            message: response.data.message,
            messageIcon: "mdi-check"
          });
        })
        .catch(error => {
          commit("toggleSnackbar", {
            show: true,
            timeout: 6000,
            color: "error",
            message: error.message,
            messageIcon: "mdi-alert-outline"
          });
        });
    },
    FOLDER_VIEW_MODE({ commit }, payload) {
      commit("setFolderViewMode", payload);
    },
    cleanFoldersData({ commit }) {
      commit("EMPTY_FOLDERS");
    },
    setActiveFolderFilterType({ commit }, payload) {
      commit("setActiveFolderFilterType", payload);
    },
    async updateInteresteds({ commit }, payload) {
      const response = await foldersService.getInteresteds(payload.folderId);
      commit("setInteresteds", response.data);
    },
  }
};

export default folder;



// WEBPACK FOOTER //
// ./src/vuex/folder/folders.js